import { type TBank } from "@/api/models/practice/bank";
import PracticeService, {
  type IPracticeFilters,
  type TPractice,
  type TPublicPractice,
} from "@/api/models/practice/practice";
import { useQuery, type UseQueryReturnType } from "@tanstack/vue-query";
import type { MaybeRefOrGetter } from "vue";
import { toRef, toValue, watch } from "vue";

import type { PaginatedResponse } from "@/api/shared";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import type {
  TItemQueryComposableParams,
  TComposableParams,
} from "@/composables/shared/filters/useGeneralFilter";
import {
  splitComposableParams,
  resolveSelectEnabled,
} from "@/composables/shared/filters/useGeneralFilter";
import type { AxiosResponse } from "axios";
import { createPathParamCrudMutations } from "@/composables/shared/mutations/useCrudMutations";
import {
  useGenericMutation,
  type TMutationComposableParams,
} from "@/composables/shared/mutations/useGenericMutation";
import { ESubjects } from "@/api/models/auth/ability";
import {
  useGenericListQuery,
  useGenericQuery,
  useGenericPaginatedQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";

export const PracticeQueryKeys = createQueryKeys(PracticeService.queryKey, (key) => ({
  all: key<IPracticeFilters>("list"),
  detail: key<string>("detail"),
  paginated: key<IPracticeFilters>("paginated"),
  byCompany: key<IPracticeFilters>("by-company"),
  gpAll: key<IPracticeFilters>("gp"),
  orthoAll: key<IPracticeFilters>("ortho"),
  related: key<IPracticeFilters>("related"),
  public: key<string>("public"),
}));

export const usePractices = (
  filters?: MaybeRefOrGetter<Partial<IPracticeFilters>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery<TPractice, Partial<IPracticeFilters>>({
    queryKey: PracticeQueryKeys.all(),
    params: { filters, enabled },
    fetcher: async (f) => {
      return await PracticeService.getAll({}, f);
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
  });
};

export const useGPPractices = (filters?: MaybeRefOrGetter<Partial<IPracticeFilters>>) => {
  return useGenericListQuery<TPractice, Partial<IPracticeFilters>>({
    queryKey: PracticeQueryKeys.gpAll(),
    params: { filters },
    fetcher: async (f) => {
      return await PracticeService.getAllGP({}, f);
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
    options: {
      refetchOnMount: true,
    },
  });
};

export const usePractice = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TPractice, string>({
    queryKey: PracticeQueryKeys.detail(),
    params,
    fetcher: async (id) => {
      return await PracticeService.get({ id });
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
    ...options,
  });
};

export const { useCreate: useCreatePractice, useDelete: useDeletePractice } =
  createPathParamCrudMutations<TPractice, { id?: string }>(PracticeService, [], {
    entityName: "practice",
    permissionSubject: ESubjects.PRACTICE__PRACTICE,
    invalidates: [
      PracticeQueryKeys.paginated(),
      PracticeQueryKeys.all(),
      PracticeQueryKeys.gpAll(),
      PracticeQueryKeys.orthoAll(),
      PracticeQueryKeys.detail(),
    ],
    removes: {
      delete: ({ id }) => [PracticeQueryKeys.detail(id)],
    },
  });

export const useUpdatePractice = (params?: TMutationComposableParams<Partial<TPractice>>) => {
  return useGenericMutation<TPractice, Partial<TPractice>>({
    mutationFn: async (data) => PracticeService.update({ id: data.id! }, data),
    feature: "update-practice",
    invalidates: [
      PracticeQueryKeys.paginated(),
      PracticeQueryKeys.all(),
      PracticeQueryKeys.gpAll(),
      PracticeQueryKeys.orthoAll(),
      PracticeQueryKeys.detail(),
    ],
    params: {
      permissions: {
        update: ESubjects.PRACTICE__PRACTICE,
      },
      neededKey: ["id"],
      ...params,
    },
  });
};

export const useBanks = (): UseQueryReturnType<TBank[], Error> => {
  return useQuery({
    queryKey: ["banks"],
    queryFn: async (): Promise<TBank[]> => {
      //TODO: Remove this once the API is ready
      return [];
      // await BankService.getAll();
    },
  });
};

//This is temporary, I will replace this once be is ready
//
export enum EPracticeStatus {
  ACTIVE = "active",
  ONBOARDING = "onboarding",
  SETUPISSUES = "setup_issues",
  DORMANT = "dormant",
}

export type TOrthoPracticeWithGenerated = TPractice & {
  status: string | undefined;
};

export const getPracticeStatusColor = (status: string) => {
  switch (status) {
    case EPracticeStatus.ACTIVE:
      return "green-lighten-4";
    case EPracticeStatus.ONBOARDING:
      return "teal-darken-3";
    case EPracticeStatus.SETUPISSUES:
      return "amber-darken-1";
    case EPracticeStatus.DORMANT:
      return "grey-lighten-2";
    default:
      return "grey";
  }
};

export const useOrthodontistPractices = (filters?: MaybeRefOrGetter<Partial<IPracticeFilters>>) => {
  return useGenericListQuery<TOrthoPracticeWithGenerated, Partial<IPracticeFilters>>({
    queryKey: PracticeQueryKeys.orthoAll(),
    params: { filters },
    fetcher: async (f) => {
      const response = await PracticeService.getAllOrtho({}, f);
      if (response) {
        response.data = response.data.map((practice: TPractice) => {
          const generatedValues = orthoPracticeGenerateTempValues(practice.id);
          return { ...practice, ...generatedValues };
        });
      }
      return response as AxiosResponse<TOrthoPracticeWithGenerated[]>;
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
    options: {
      refetchOnWindowFocus: true,
    },
  });
};

export const usePracticePaginated = (params: TOwnedFilterParams<IPracticeFilters> = {}) => {
  return useGenericPaginatedQuery<TOrthoPracticeWithGenerated, Partial<IPracticeFilters>>({
    params,
    queryKey: PracticeQueryKeys.paginated(),
    fetcher: async (fetchParams) => {
      let response: PaginatedResponse<TPractice> | null = null;
      if (fetchParams.filters.type === undefined) {
        response = await PracticeService.getPaginatedFiltered(
          {},
          fetchParams.filters,
          fetchParams.pageParam
        );
      } else if (fetchParams.filters.type === "orthodontist") {
        response = await PracticeService.getPaginatedOrtho(
          {},
          fetchParams.filters,
          fetchParams.pageParam
        );
      } else if (fetchParams.filters.type === "gp") {
        response = await PracticeService.getPaginatedGP(
          {},
          fetchParams.filters,
          fetchParams.pageParam
        );
      }

      if (response) {
        response.results = response.results.map((practice: TPractice) => {
          const generatedValues = orthoPracticeGenerateTempValues(practice.id);
          return { ...practice, ...generatedValues };
        });
      }
      return response as PaginatedResponse<TOrthoPracticeWithGenerated>;
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
  });
};

export const usePracticeRelated = (
  filters: MaybeRefOrGetter<Partial<IPracticeFilters> & { practice_id?: string }>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery<TPractice, Partial<IPracticeFilters & { practice_id?: string }>>({
    queryKey: PracticeQueryKeys.related(),
    params: { filters, enabled },
    fetcher: async (f) => {
      const { practice_id, ...queryFilters } = f ?? {};
      return await PracticeService.getRelatedPractices({ practice_id: practice_id! }, queryFilters);
    },
    permission: $permSubjects.PRACTICE__PRACTICE,
    neededKey: ["practice_id"],
  });
};

function orthoPracticeGenerateTempValues(id: string) {
  const seed = parseInt(id.substring(0, 8), 16);

  const statuses = ["active", "dormant", "setup_issues", "onboarding"];
  const statusIndex = seed % statuses.length;
  const status = statuses[statusIndex];

  return {
    status,
  };
}

export const usePublicPractice = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TPublicPractice, string>({
    queryKey: PracticeQueryKeys.public(),
    params,
    fetcher: async (id) => {
      return await PracticeService.public.get({ id });
    },
    ...options,
  });
};

export const usePracticeForSelect = (
  extraParams?: MaybeRefOrGetter<Partial<IPracticeFilters> & TComposableParams>
): TSelectFieldDynamicProps<TPractice, IPracticeFilters> => {
  const refExtraParams = toRef(extraParams);
  const { filters: initialFilters } = splitComposableParams(toValue(refExtraParams));

  const query = usePracticePaginated({
    initial: initialFilters,
    // Kept as a getter so a reactive `enabled` stays live; `useOwnedFilterRefs` reads
    // through it and the select can still write the lazy gate open.
    enabled: () => resolveSelectEnabled(toValue(refExtraParams)),
  });

  watch(refExtraParams, (val) => {
    const { filters: nextFilters } = splitComposableParams(val);
    query.filters.value = { ...query.filters.value, ...nextFilters };
  });

  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    filterField: "query",
    queryEnabled: query.queryEnabled,
  };
};
